/**
 * Field formatters — Company Info & elsewhere.
 *
 * Each formatter follows the same shape:
 *   - `format(digitsOrRaw)`      → human display string
 *   - `normalize(anyInput)`      → canonical storage string
 *   - `validate(canonicalValue)` → { ok, message } (empty = ok)
 *
 * All formatters accept messy input (spaces, hyphens, dashes, lowercase)
 * and return clean output. Storage is always the compact canonical form
 * so the DB never mixes formatted / unformatted values.
 */

// ---------------------------------------------------------------------------
// North American phone → (###) ###-####
// ---------------------------------------------------------------------------
export function normalizePhone(raw) {
  if (!raw) return "";
  return String(raw).replace(/\D/g, "");
}

export function formatPhone(raw) {
  const d = normalizePhone(raw);
  if (!d) return "";
  // Trim to at most 10 digits for the standard NANP display. If the user
  // typed an 11-digit "1" prefix or extension, we still format the LAST
  // 10 digits so the on-screen shape stays predictable.
  const use = d.length > 10 ? d.slice(-10) : d;
  const a = use.slice(0, 3);
  const b = use.slice(3, 6);
  const c = use.slice(6, 10);
  if (use.length <= 3) return a ? `(${a}` : "";
  if (use.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

export function validatePhone(value) {
  const d = normalizePhone(value);
  if (!d) return { ok: true };
  if (d.length < 10) return { ok: false, message: "Phone number must have 10 digits." };
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Canadian GST/HST business number → ######### RT ####
// ---------------------------------------------------------------------------
export function normalizeHst(raw) {
  if (!raw) return "";
  // Strip whitespace, upper-case any letters. Keep digits + letters so
  // an intermediate value like "733496822R" still round-trips as the
  // user types.
  return String(raw).replace(/\s+/g, "").toUpperCase();
}

export function formatHst(raw) {
  const compact = normalizeHst(raw);
  if (!compact) return "";
  // Progressive formatting: apply spaces as soon as the user has typed
  // enough characters, so the field looks canonical while typing.
  //   ≤9   →  raw digits so far
  //   9<x≤11 → "######### RT" (letters land automatically)
  //   >11  → "######### RT ####"
  const bn = compact.slice(0, 9);
  const rt = compact.slice(9, 11);
  const acct = compact.slice(11, 15);
  if (compact.length <= 9) return bn;
  if (compact.length <= 11) return `${bn} ${rt}`;
  return `${bn} ${rt} ${acct}`;
}

export function validateHst(value) {
  const compact = normalizeHst(value);
  if (!compact) return { ok: true };
  if (!/^\d{9}RT\d{4}$/.test(compact)) {
    return {
      ok: false,
      message: "Must be 9 digits, then 'RT', then 4 digits (e.g. 733496822 RT 0001).",
    };
  }
  return { ok: true };
}
